/**
 * useStagedSendBatch — the staged-send card's live rows (2026-09-17).
 *
 * Owner: "a draft box stays a draft updated for all users in thread instantly
 * as user makes a change." The card's truth therefore has to be the pending
 * action DOCUMENTS, not a snapshot the browser happened to fetch once.
 *
 * Three facts make this cheap, all read from the tree rather than assumed:
 *  - `accounts/{accountId}/pending_actions/{id}` is already client-READABLE by
 *    account members and write-denied (firestore.rules), and the rule places no
 *    constraint on the query shape. No rules change, so the provisioning-impact
 *    gate is untouched.
 *  - A single `where('batchId','==',X)` rides Firestore's automatic index.
 *    Ordering happens IN MEMORY on purpose: an `orderBy` on a different field
 *    is the only thing here that would force a composite index, and the tile
 *    already sorts its rows.
 *  - The tile's message payload already carries `batchId`, and the byte-budget
 *    backstop deliberately never touches `payload`.
 *
 * It also replaces N `peekPendingAction` round trips per rehydrate with one
 * query — the old cost at forty staged emails.
 */

import { useEffect, useRef, useState } from 'react';
import { collection, getFirestore, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getActiveWorkspaceAccount, onActiveWorkspaceResolved } from '../../activeWorkspace';
import { isRetryableRbacReadError } from '../../../hooks/rbacReadError';
import { rowFieldsFromPendingPreview, rowStateFromPeek } from './batchSendTile';
import type { StagedSendRow } from './authorizeCardHtml';

/** The shared ladder the thread listeners use; a transient error backs off
 *  rather than hammering, and a settled listener resets the index. */
const RETRY_DELAYS_MS = [1_000, 5_000, 15_000, 30_000];

export type StagedSendBatchStatus = 'idle' | 'live' | 'unavailable';

export interface StagedSendBatchView {
  rows: StagedSendRow[] | null;
  status: StagedSendBatchStatus;
}

/**
 * Live rows for one staged-send batch.
 *
 * `status: 'unavailable'` is the honest signal that the listener cannot serve —
 * a permission refusal, or no resolved workspace — and is what the tile falls
 * back to the peek path on. It is NOT an error to retry: `permission-denied` is
 * deterministic (rules evaluate identically on a retry), which every existing
 * listener in this repository gets wrong by retrying forever. `rbacReadError`
 * already owns that predicate for one-shot reads; this is the same rule applied
 * where it has been missing.
 */
export function useStagedSendBatch(batchId: string | null | undefined): StagedSendBatchView {
  const [rows, setRows] = useState<StagedSendRow[] | null>(null);
  const [status, setStatus] = useState<StagedSendBatchStatus>('idle');
  // Kept in a ref so a re-render never restarts the backoff ladder.
  const retryIndexRef = useRef(0);

  useEffect(() => {
    if (!batchId) {
      setRows(null);
      setStatus('idle');
      return undefined;
    }
    let stopped = false;
    let detach: Unsubscribe | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let waitingForWorkspace: (() => void) | null = null;

    const listen = (accountId: string): void => {
      if (stopped) return;
      try {
        const db = getFirestore(getApp());
        detach = onSnapshot(
          query(
            collection(db, 'accounts', accountId, 'pending_actions'),
            where('batchId', '==', batchId),
          ),
          (snap) => {
            if (stopped) return;
            retryIndexRef.current = 0;
            const next: StagedSendRow[] = [];
            snap.forEach((docSnap) => {
              const data = docSnap.data() as Record<string, any>;
              if (!data?.preview) return;
              // A hold that was dead on arrival renders no row — its
              // replacement carries the draft (the tile's own long-standing
              // rule, kept here so the live lane and the peek lane agree).
              const decided = rowStateFromPeek({
                status: String(data.status || 'pending'),
                confirmation: data.confirmation || null,
                autoApproved: data.autoApproved === true,
                retryable: data.retryable === true,
              });
              if (decided.state === 'superseded') return;
              next.push({
                actionId: docSnap.id,
                batchIndex: Number(data.batchIndex) || 0,
                ...rowFieldsFromPendingPreview(data.preview as Record<string, any>),
                ...decided,
              });
            });
            next.sort((a, b) => (a.batchIndex - b.batchIndex) || a.actionId.localeCompare(b.actionId));
            setRows(next);
            setStatus('live');
          },
          (err: unknown) => {
            if (stopped) return;
            try { detach?.(); } catch { /* already gone */ }
            detach = null;
            if (!isRetryableRbacReadError(err)) {
              // Deterministic refusal: retrying re-runs the same rules against
              // the same person for the same answer. Hand the tile back to the
              // peek path instead of looping.
              setStatus('unavailable');
              return;
            }
            const delay = RETRY_DELAYS_MS[Math.min(retryIndexRef.current, RETRY_DELAYS_MS.length - 1)];
            retryIndexRef.current += 1;
            retryTimer = setTimeout(() => listen(accountId), delay);
          },
        );
      } catch {
        // A synchronous construction throw is the same class as an error
        // callback — back off rather than leaving the tile with no rows.
        const delay = RETRY_DELAYS_MS[Math.min(retryIndexRef.current, RETRY_DELAYS_MS.length - 1)];
        retryIndexRef.current += 1;
        retryTimer = setTimeout(() => listen(accountId), delay);
      }
    };

    // `null` means "not resolved yet", never "no account" — the workspace
    // mirror publishes once and does not replay, so read it first and only
    // subscribe when it is genuinely still pending.
    const account = getActiveWorkspaceAccount();
    if (account) {
      listen(account);
    } else {
      waitingForWorkspace = onActiveWorkspaceResolved((resolved) => {
        if (!stopped && resolved) listen(resolved);
      });
    }

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { detach?.(); } catch { /* already gone */ }
      try { waitingForWorkspace?.(); } catch { /* not subscribed */ }
    };
  }, [batchId]);

  return { rows, status };
}
